import React, {useEffect, useState} from "react";
import {DatePicker, Select, Space, Switch, Tag, Typography} from "antd";
import {ExtendedDataProvider, GeneralFilterProps, GeneralFilterSelectItem, SelectItem} from "../../types";
import {useDataProvider} from "react-admin";
import en_GB from 'antd/locale/en_GB';
const locale = en_GB.DatePicker;
import useGetGeneralSettings from "@src/providers/useGetGeneralSettings";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

const MIN_WIDTH = "250px";
const MIN_WIDTH_2 = "516px"
const MIN_WIDTH_3 = "400px"

const DecommissionedTag = ({ runtimeState }: { runtimeState?: string }) => {
    if (runtimeState !== 'DECOMMISSIONED') return null;

    return (
        <Tag
            color="magenta"
            style={{
                fontSize: '11px',
                padding: '0 6px',
                borderRadius: '10px',
                fontWeight: 500,
                margin: 0
            }}
        >
            Decommissioned
        </Tag>
    );
};

const GeneralFilter = ({
                           filterValues,
                           setFilterValues,
                           applicationsResource,
                           serviceItemsResource,
                           teamsResource,
                           servicesResource,
                           style,
                           spaceDirection = "horizontal",
                           stateFilterEnabled = true,
                           changeTypeFilterEnabled = true,
                           modifiedFilterEnabled = false,
                           dateRangeFilterEnabled = false
                       }: GeneralFilterProps) => {
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const generalSettings = useGetGeneralSettings();



    useEffect(() => {
        const GUITableSettings = generalSettings.find(setting => setting.name == 'GUITableSettings')
        if (stateFilterEnabled==true){
            var initialStateFilters = ['PENDING', 'APPROVED']
            var GSStateFilters = undefined
            if(GUITableSettings!=undefined){
                GSStateFilters = (
                    serviceItemsResource==="owner_service_items"?
                        GUITableSettings?.config?.default_filter_states_for_serviceowners
                        :
                        GUITableSettings?.config?.default_filter_states_for_consumers
                )
            }
            if(GSStateFilters !== undefined)
                setFilterValues({ ...filterValues, 'state': GSStateFilters.join() }, {})

            else
                setFilterValues({ ...filterValues, 'state': initialStateFilters.join() }, {})
        } else {
            // Remove state from filter params if stateFilterEnabled is false
            const { state, ...newFilterValues } = filterValues;
            setFilterValues(newFilterValues, {});
        }
    }, [])

    const [teams, setTeams] = useState<SelectItem[] | null>(null)
    const [applications, setApplications] = useState<GeneralFilterSelectItem[] | null>(null)
    const [serviceItems, setServiceItems] = useState<GeneralFilterSelectItem[] | null>(null)
    const [services, setServices] = useState<SelectItem[] | null>(null)

    const [selectedChangeTypes, setSelectedChangeTypes] = useState<any>(['CREATE', 'MODIFY', 'DELETE'])

    const [initialApplication, setInitialApplication] = useState<string|null>(filterValues.application_id)
    const [initialServiceItem, setInitialServiceItem] = useState<string|null>(filterValues.service_item_id)

    const [includeDecommissionedApplications, setIncludeDecommissionedApplications] = useState<boolean>(false)
    const [includeDecommissionedServiceItems, setIncludeDecommissionedServiceItems] = useState<boolean>(false)


    const StatesData: string[] = ['PENDING', 'APPROVED', 'COMPLETED', 'ERROR', 'CLOSED', 'REJECTED'];
    const ChangeTypes = ['CREATE', 'MODIFY', 'DELETE'];

    const onConsumerSelect = (value: string) => {
        setFilterValues({ ...filterValues, 'consumer_team_id': value, 'application_id': '', 'service_item_id': ''}, {})
    }

    const onAppSelect = (value: string) => {
        setFilterValues({ ...filterValues, 'application_id': value, 'service_item_id': '' })
    }

    const onServiceItemSelect = (value: string) => {
        setFilterValues({ ...filterValues, 'service_item_id': value })
    }

    const onServiceSelect = (value: string) => {
        setFilterValues({ ...filterValues, 'service_id': value })
    }

    const handleChangeTypeChange = (change_type: any, checked: boolean) => {
        const nextSelectedChangeTypes = checked ? [...selectedChangeTypes, change_type] : selectedChangeTypes.filter((t: any) => t !== change_type);
        setSelectedChangeTypes(nextSelectedChangeTypes)
        setFilterValues({ ...filterValues, 'change_type': nextSelectedChangeTypes.join() })
    }

    const timeRangeOnChange = (dates: any , dateStrings: any) => {
        var range_start = '';
        var range_end = '';
        if (dates) {
            if (dates[0]) {
                const range_start_ = dates[0];
                range_start_.set ('hour', 0);
                range_start_.set ('minute', 0);
                range_start_.set ('second', 0);
                range_start_.set ('millisecond', 0);
                range_start = range_start_.toISOString ()
            }

            if (dates[1]) {
                const range_end_ = dates[1]
                range_end_.set ('hour', 23);
                range_end_.set ('minute', 59);
                range_end_.set ('second', 59);
                range_end_.set ('millisecond', 0);
                range_end = range_end_.toISOString ()
            }
        }

        setFilterValues({
            ...filterValues,
            range_after: range_start,
            range_before: range_end
        }, {});
    }
    
    useEffect(() => {
        if(teamsResource){
            dataProvider.getFilterOptions(
                teamsResource,
                {
                    pagination: { page: 1, perPage: 0 },
                    sort: { field: 'name', order: 'ASC' },
                    filter: {}
                })
                .then(({ data }) => {
                    const teamFilter: SelectItem[] | null = []
                    if (data) {
                        for (let item of data) {
                            teamFilter.push({ 'text': item.name, 'value': item.id.toString() })
                        }
                        setTeams(teamFilter);
                    }
                })
                .catch(error => {console.log(error)})
        }
    }, [teamsResource, filterValues?.state, selectedChangeTypes])

    useEffect(() => {
        if(applicationsResource){
            dataProvider.getFilterOptions(
                applicationsResource,
                {
                    pagination: { page: 1, perPage: 0 },
                    sort: { field: "", order: '' },
                    filter: {
                        'consumer_team_id': filterValues.consumer_team_id,
                        'include_decommissioned': includeDecommissionedApplications
                    }
                })
                .then(({ data }) => {
                    const appFilter: GeneralFilterSelectItem[] = []
                    if (data) {
                        for (let item of data) {
                            appFilter.push({ 'text': item.name, 'value': item.id.toString(), 'runtime_state': item.runtime_state })
                            if(item.id.toString()===initialApplication)
                                setInitialApplication(item.name)
                        }
                        setApplications(appFilter);
                    }
                })
                .catch(error => {console.log(error)})
        }
    }, [filterValues.consumer_team_id, applicationsResource, filterValues?.state, selectedChangeTypes, includeDecommissionedApplications])

    useEffect(() => {
        if(serviceItemsResource){
            dataProvider.getFilterOptions(
                serviceItemsResource,
                {
                    pagination: { page: 1, perPage: 0 },
                    sort: { field: '', order: '' },
                    filter: {
                        'application_id': filterValues.application_id,
                        'include_decommissioned': includeDecommissionedServiceItems
                    }
                })
                .then(({ data }) => {
                    const serviceItemFilter: GeneralFilterSelectItem[] = []
                    if (data) {
                        for (let item of data) {
                            serviceItemFilter.push({ 'text': item.name, 'value': item.id.toString(), 'runtime_state': item.runtime_state })
                            if(item.id.toString()===initialServiceItem)
                                setInitialServiceItem(item.name)
                        }
                        setServiceItems(serviceItemFilter);
                    }
                })
                .catch(error => {console.log(error)})
        }
    }, [filterValues.application_id, serviceItemsResource, filterValues?.state, selectedChangeTypes, includeDecommissionedServiceItems])

    useEffect(() => {
        if(servicesResource){
            dataProvider.getFilterOptions(
                servicesResource,
                {
                    pagination: { page: 1, perPage: 0 },
                    sort: { field: 'name', order: 'ASC' },
                    filter: {}
                })
                .then(({ data }) => {
                    const serviceFilter: any[] = []
                    if (data) {
                        for (let item of data) {
                            serviceFilter.push({ 'text': item.name, 'value': item.id })
                        }
                        setServices(serviceFilter);
                    }
                })
                .catch(error => {console.log(error)})
        }
    }, [filterValues, servicesResource, filterValues?.state, selectedChangeTypes])

    return (
            <Space
                orientation="vertical"
                size="middle"
                style={{
                    padding: '16px 24px 16px 12px',
                    // backgroundColor: '#fafafa',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    width: '100%',
                    ...style
                }}
            >
                {/* First line: Selects */}
                <Space
                    orientation={spaceDirection}
                    wrap
                    size="middle"
                    style={spaceDirection === "vertical" ? { width: '100%' } : undefined}
                >
                    {teamsResource && (
                        <Select
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) => {
                                const children = option?.children;
                                return children ? String(children).toLowerCase().includes(input.toLowerCase()) : false;
                            }}
                            value={filterValues.consumer_team_id}
                            placeholder="Select Consumer"
                            className="filter-select"
                            style={{ minWidth: MIN_WIDTH, width: spaceDirection === "vertical" ? '100%' : undefined }}
                            onChange={onConsumerSelect}
                        >
                            <Option key={0} value="">All Teams</Option>
                            {teams && teams.map(team => <Option key={team.value} value={team.value}>{team.text}</Option>)}
                        </Select>
                    )}

                    {applicationsResource && (
                        <Select
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) => {
                                const children = option?.children;
                                return children ? String(children).toLowerCase().includes(input.toLowerCase()) : false;
                            }}
                            disabled={!filterValues.consumer_team_id && !!teamsResource}
                            defaultValue={initialApplication}
                            data-cy="application-selector"
                            placeholder="Select Application"
                            className="filter-select"
                            style={{ minWidth: MIN_WIDTH, width: spaceDirection === "vertical" ? '100%' : undefined }}
                            onChange={onAppSelect}
                        >
                            <Option key={0} value="">All Apps</Option>
                            {applications && applications.map(app => <Option key={app.value} value={app.value}>{app.text}</Option>)}
                        </Select>
                    )}

                    {serviceItemsResource && (
                        <Select
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) => {
                                const children = option?.children;
                                return children ? String(children).toLowerCase().includes(input.toLowerCase()) : false;
                            }}
                            disabled={!filterValues.application_id}
                            defaultValue={initialServiceItem}
                            placeholder="Select Service Item"
                            className="filter-select"
                            style={{ minWidth: MIN_WIDTH, width: spaceDirection === "vertical" ? '100%' : undefined }}
                            onChange={onServiceItemSelect}
                        >
                            <Option value="">All Service Items</Option>
                            {serviceItems && serviceItems.map(service => <Option key={service.value} value={service.value}>{service.text}</Option>)}
                        </Select>
                    )}

                    {servicesResource && (
                        <Select
                            data-cy="service-selector"
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) => {
                                const children = option?.children;
                                return children ? String(children).toLowerCase().includes(input.toLowerCase()) : false;
                            }}
                            value={filterValues.service_id}
                            placeholder="Select Service"
                            className="filter-select"
                            style={{ minWidth: MIN_WIDTH, width: spaceDirection === "vertical" ? '100%' : undefined }}
                            onChange={onServiceSelect}
                        >
                            <Option key={0} value="">All Services</Option>
                            {services && services.map(service => <Option key={service.value} value={service.value}>{service.text}</Option>)}
                        </Select>
                    )}

                    {stateFilterEnabled && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: MIN_WIDTH_2, width: spaceDirection === "vertical" ? '100%' : undefined }}>
                            <strong style={{ whiteSpace: 'nowrap' }}>States:</strong>
                            <Select
                                mode="multiple"
                                placeholder="Select states"
                                value={filterValues?.state?.split(",")}
                                onChange={(value: any) => setFilterValues({ ...filterValues, 'state': value.join() }, {})}
                                className="filter-select"
                                style={{ width: '100%' }}
                                maxTagCount={5}
                                maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
                                options={StatesData.map((item) => ({ value: item, label: item }))}
                            />
                        </div>
                    )}

                    {changeTypeFilterEnabled && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: MIN_WIDTH_3, width: spaceDirection === "vertical" ? '100%' : undefined }}>
                            <strong style={{ whiteSpace: 'nowrap' }}>Change Types:</strong>
                            <Select
                                mode="multiple"
                                placeholder="Select change types"
                                value={selectedChangeTypes}
                                onChange={(value: any) => {
                                    setSelectedChangeTypes(value);
                                    setFilterValues({ ...filterValues, 'change_type': value.join() }, {});
                                }}
                                className="filter-select"
                                style={{ width: '100%' }}
                                maxTagCount={3}
                                maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
                                options={ChangeTypes.map((item) => ({ value: item, label: item }))}
                            />
                        </div>
                    )}

                    {modifiedFilterEnabled && (
                        <Space>
                            <strong>Last modified since:</strong>
                            <DatePicker
                                locale={locale}
                                showTime={{ showSecond: false }}
                                onChange={(date: any) => {
                                    if (date)
                                        setFilterValues({ ...filterValues, 'modified': date.toISOString() }, {})
                                    else
                                        setFilterValues({ ...filterValues, 'modified': '' }, {})
                                }}
                            />
                        </Space>
                    )}

                    {dateRangeFilterEnabled && (
                        <Space orientation="vertical" style={{ width: spaceDirection === "vertical" ? '100%' : undefined }}>
                            <Text><strong>Filter by date:</strong></Text>
                            <RangePicker
                                style={{ width: "100%" }}
                                onChange={timeRangeOnChange}
                                allowEmpty={[true, true]}
                            />
                        </Space>
                    )}
                </Space>
            </Space>
    )
}

export default GeneralFilter;
